import {
  Component,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
  DestroyRef,
} from '@angular/core';
import {
  ModalBaseConfig,
  ModalBaseComponent,
} from '@app/core/components/modal-base/modal-base.component';
import { AdditionalFieldsGQL, Company, Order } from '@app/generated/graphql';
import { Modal } from '@app/core/interfaces/modal.interface';
import { ToastService } from '@app/core/services/toast/toast.service';
import { NewOrderStore } from '@app/account/new-order/stores/new-order.store';
import { clone } from '@app/core/util/clone';
import { ModalBaseComponent as ModalBaseComponent_1 } from '../../../core/components/modal-base/modal-base.component';
import { Tooltip } from 'primeng/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type ViewAction = 'accept' | 'wont pack' | 'save order';

type FormMode = 'Edit';

export interface AdditionalFieldsMismatchConfig extends ModalBaseConfig {
  company: Company;
  order: Order;
}

@Component({
  selector: 'ag-additional-fields-mismatch-modal',
  templateUrl: './additional-fields-mismatch-modal.component.html',
  styleUrls: ['./additional-fields-mismatch-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ModalBaseComponent_1, Tooltip, AngularSvgIconModule],
})
export class AdditionalFieldsMismatchModalComponent implements OnInit, Modal {
  private _destroyRef = inject(DestroyRef);
  private additionalFieldsGQL = inject(AdditionalFieldsGQL);
  private toastService = inject(ToastService);
  private newOrderStore = inject(NewOrderStore);

  @ViewChild(ModalBaseComponent, { static: true })
  modalBase: ModalBaseComponent;

  company: Company;
  order: Order;
  hasMismatch = true;
  formMode: FormMode = 'Edit';
  clearAdditionalField: string;
  clearingAdditionalFieldData = false;

  currentAdditionalFields: string[];
  allowedAdditionalFields: string[];
  clearableFields: string[];

  constructor() {}

  ngOnInit() {
    this.modalBase.setLoading(true);
    this.getAdditionalFieldsFromLineItems();
    this.fetchAdditionalFieldList();
  }

  show = () => this.modalBase.show();
  hide = () => this.modalBase.hide();
  setLoading = (isLoading: boolean) => this.modalBase.setLoading(isLoading);

  configure(config: AdditionalFieldsMismatchConfig) {
    this.company = config.company;
    this.order = config.order;
    this.modalBase.configure(config);
  }

  fetchAdditionalFieldList() {
    this.hasMismatch = true;
    this.additionalFieldsGQL
      .fetch({
        variables: { companyids: [this.company.id] },
        fetchPolicy: 'network-only',
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(
        (res) => {
          this.company.additionalFields = clone(res.data.additionalFields);
          this.allowedAdditionalFields = res.data.additionalFields.map(
            (x) => x.name,
          );
          this.calculateMismatch();
        },
        (err) => {
          this.toastService.showErrorToast('Unable to get mismatch data');
          throw err;
        },
      )
      .add(() => {
        this.setLoading(false);
      });
  }

  showClearAdditionalFieldData(field: string) {
    this.clearAdditionalField = field;
  }

  getAdditionalFieldsFromLineItems() {
    this.currentAdditionalFields = [
      ...new Set(
        this.order.lineItemGroupings
          .reduce((arr, group) => arr.concat(group.lineItems), [])
          .map((item) => Object.keys(item.additionalFields))
          .reduce((arr, keys) => arr.concat(keys, [])),
      ),
    ];
  }

  calculateMismatch() {
    this.clearableFields = this.currentAdditionalFields.filter(
      (x) => !this.allowedAdditionalFields.includes(x),
    );
    if (!this.clearableFields || this.clearableFields.length == 0) {
      this.hasMismatch = false;
    }
  }

  removeField() {
    this.clearingAdditionalFieldData = true;
    this.order.lineItemGroupings.map((group) => {
      group.lineItems.map(
        (item) => delete item.additionalFields[this.clearAdditionalField],
      );
    });
    this.clearableFields = this.clearableFields.filter(
      (field) => field !== this.clearAdditionalField,
    );
    this.toastService.showToast(
      `${this.clearAdditionalField} removed from all line items from this order`,
    );
    this.clearAdditionalField = null;
    if (!this.clearableFields || this.clearableFields.length == 0) {
      this.hasMismatch = false;
    }
    this.newOrderStore.set(this.order);
    this.clearingAdditionalFieldData = false;
  }
}
